/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { ReactComponent as PlusIcon } from "@shared/assets/Icons/IconPlusNV.svg";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useAppSelector } from "@redux/hooks";
import { addEmailChannel, fetchCustomersServices, updateEmailChannel } from "./requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";

export interface UserCheckResponse {
  exists: boolean;
  message: string;
}

export interface DefaultRecipientsManagerProps {
  subscriptionId?: string;
  notificationChannels?: Array<{
    id: string;
    channelType: string;
    channelConfig: { emails: string[] };
    enabled: boolean;
  }>;
  initialRecipients: string[];
  onClose?: () => void;
  onRecipientsChange?: (recipients: string[]) => void;
  checkUserExists?: (email: string) => Promise<UserCheckResponse>;
  showControls?: boolean;
  onSaveSuccess?: () => void;
}

export const DefaultRecipientsManager: React.FC<
  DefaultRecipientsManagerProps
> = ({
  initialRecipients,
  onRecipientsChange,
  onClose,
  checkUserExists,
  showControls,
  notificationChannels,
  subscriptionId,
  onSaveSuccess,
}) => {
  const api = useAxiosAuth0();

  const [recipients, setRecipients] = useState<string[]>([
    ...initialRecipients,
  ]);
  const [removedEmails, setRemovedEmails] = useState<string[]>([]);

  const [newEmail, setNewEmail] = useState<string>("");
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<{
    type: "error" | "info";
    message: string;
  } | null>(null);
  const { tenantId, partnerId, tenantDomain, partnerDomain } = useAppSelector(s => s.selection);
  const tenantType  = useAppSelector(s => s.userProfile.TenantType);

  const [serviceDomain, setServiceDomain] = useState<string>("");
  const isCustomerTenant = tenantId !== partnerId;


  const [pendingEmails, setPendingEmails] = useState<string[]>([]);
  const [initialPendingSnapshot, setInitialPendingSnapshot] = useState<
    string[]
  >([]);

  const domainString: string = isCustomerTenant
    ? (serviceDomain || tenantDomain || "")
    : (tenantDomain || partnerDomain || "");

  // const domainString = tenantDomain || partnerDomain || "";
  console.log(domainString, 'emaildomain');
  const allowedDomains = (domainString || "")
  .split(/[;,]/)
  .map(d => d.trim().toLowerCase())
  .filter(Boolean);

  function getBackendErrorMessage(err: any): string {
    const data = err.response?.data;
    //If we have a human‐readable message
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
    //Fall back to an “error” field
    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error;
    }
    //Include domain‐specific arrays (e.g. invalidEmails)
    if (Array.isArray(data?.invalidEmails) && data.invalidEmails.length) {
      return `Invalid items: ${data.invalidEmails.join(", ")}`;
    }
    //Generic fallback
    return "An unexpected error occurred. Please try again.";
  }

  useEffect(() => {
    if (isCustomerTenant && tenantId) {
      fetchCustomersServices(api, tenantId)
        .then(resp => {
          const data = resp.data.data;
          if (Array.isArray(data) && data.length > 0) {
            setServiceDomain(data[0].Domain || "");
          }
        })
        .catch(err => {
          console.error("Failed to load customer domain", err);
        });
    }
  }, [isCustomerTenant, tenantId, api]);

  useEffect(() => {
    setRecipients([...initialRecipients]);
  }, [initialRecipients]);

  const handleConfirmRecipients = async () => {
    if (!subscriptionId) {
      setAlertMessage({ type: "error", message: "No subscription selected." });
      return;
    }
    //Coerce possibly‐undefined channels into an array
    const channels = notificationChannels ?? [];
    //Build final email list
    const allEmails = [...recipients, ...pendingEmails];
    setIsLoading(true);
    try {
      if (channels.length === 0) {
        //No channel exists → POST a new one
        await addEmailChannel(api, subscriptionId, allEmails);
      } else {
        //Update the first existing channel
        const channelId = channels[0].id;
        await updateEmailChannel(api, subscriptionId, channelId, allEmails);
      }
      //Bubble the change up to the parent
      onRecipientsChange?.(allEmails);

      //Show a success banner
      setAlertMessage({
        type: "info",
        message: "Recipients saved successfully!",
      });

      // reset local lists
      setRecipients(allEmails);
      setPendingEmails([]);

      // close in 3s so they see the banner
      onSaveSuccess?.();
    } catch (err: any) {
      const userMsg = getBackendErrorMessage(err);
      setAlertMessage({ type: "error", message: userMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPendingEmails(initialPendingSnapshot);
    setNewEmail("");
    setIsAdding(false);
    setAlertMessage(null);
  };

  // We remove mail from the list when it's clicked
  const handleRemove = (email: string) => {
    const updated = recipients.filter((e) => e !== email);
    setRecipients(updated);
    onRecipientsChange?.(updated);
    setAlertMessage(null);

    //Push into removedEmails
    setRemovedEmails((prev) =>
      prev.includes(email) ? prev : [...prev, email]
    );
  };

  // Whenever a “removed” mail is clicked, put it back
  const handleRestore = (email: string) => {
    setRemovedEmails((prev) => prev.filter((e) => e !== email));
    setRecipients((prev) => [...prev, email]);
    setAlertMessage(null);
  };

  const hasChanges = pendingEmails.length > 0 || removedEmails.length > 0;

  const handleRemovePending = (email: string) => {
    setPendingEmails((prev) => prev.filter((e) => e !== email));
  };

  const handlePendingSubmit = async () => {
    const trimmed = newEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmed || !emailRegex.test(trimmed)) {
      setAlertMessage({
        type: "error",
        message: "Enter a valid email address.",
      });
      return;
    }

    if ([...recipients, ...pendingEmails].includes(trimmed)) {
      setAlertMessage({
        type: "error",
        message: "This email is already added.",
      });
      return;
    }

    // 2) enforce same domain
    const domainPart = trimmed.split("@")[1]?.toLowerCase() || "";
      if (!allowedDomains.includes(domainPart)) {
        setAlertMessage({
          type: "error",
          message:"Only emails from your organization are allowed"
        });
        return;
    }
    // If not internal, or internal but not a user
    setPendingEmails((prev) => [...prev, trimmed]);
    // 4) reset the input
    setIsAdding(false);
    setNewEmail("");
    setAlertMessage(null);
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {alertMessage && (
        <div
          className={`p-2 mb-2 rounded text-sm border ${
            alertMessage.type === "error"
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-blue-50 border-blue-200 text-blue-700"
          }`}
        >
          {alertMessage.message}
        </div>
      )}
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex flex-wrap gap-2">
        {recipients
          .filter((email) => !pendingEmails.includes(email))
          .map((email) => (
            <div key={email}>
              <GenericTag
                key={email}
                variant="neutral"
                rounded="full"
                size="small"
                label={email}
                removable
                onRemove={() => handleRemove(email)}
              />
            </div>
          ))}
        {pendingEmails.map((email) => (
          <GenericTag
            key={email}
            variant="default"
            rounded="full"
            size="small"
            label={email}
            removable
            onRemove={() => handleRemovePending(email)}
          />
        ))}

        {isAdding ? (
          <div className="flex items-center space-x-2">
            <GenericInput
              value={newEmail}
              onChange={(value) => setNewEmail(value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  handlePendingSubmit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setIsAdding(false);
                  setNewEmail("");
                  setAlertMessage(null);
                }
              }}
              disabled={isLoading}
              placeholder="Enter email address"
              className="text-sm"
            />
          </div>
        ) : (
          <GenericButton
            variant="dashed"
            label="Add more people..."
            size="sm"
            shape="ghost"
            onClick={() => {
              setInitialPendingSnapshot(pendingEmails);
              setIsAdding(true);
            }}
            icon={<PlusIcon className="w-4" />}
          />
        )}
      </div>
      {removedEmails.length > 0 && (
        <div className="mt-2 px-3">
          <p className="text-xs text-semantic-text-primary font-medium">
            Mails removed:
          </p>
          <div className="flex flex-wrap gap-2 mt-1">
            {removedEmails.map((email) => (
              <GenericTag
                key={email}
                variant="error"
                rounded="full"
                size="small"
                label={email}
                onClick={() => handleRestore(email)}
              />
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-between w-full mt-3">
        <div className="column">
          <p className="text-xs text-semantic-text-secondary mt-2">
            • You can add more emails by pressing &quot;Enter&quot;.
          </p>
          <p className="text-xs text-semantic-text-secondary mt-2">
            • Only addresses at{" "}
            {allowedDomains.map((d) => `@${d}`).join(" or ")} are accepted.
          </p>
          <p className="text-xs text-semantic-text-secondary mt-2">
            • Registered users can manage and customize their own alert
            preferences.
          </p>
        </div>
        {showControls !== false && (
          <div className="flex gap-x-2">
            <GenericButton
              size="sm"
              variant="secondary"
              onClick={handleCancel}
              label="Cancel"
              disabled={isLoading}
            />
            <GenericButton
              size="sm"
              onClick={handleConfirmRecipients}
              disabled={!hasChanges || isLoading}
              label="Confirm recipients changes"
              loading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DefaultRecipientsManager;
